import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete as IAutocomplete,
  Chip,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import { useRef } from 'react';
import { FieldArray } from 'formik';
import { isValidArray } from '../../../utils/valid';

interface IProps {
  isAddMode: boolean;
  response: any;
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: any;
}

const DetailsForm: React.FC<IProps> = ({
  isAddMode,
  response,
  values,
  handleChange,
  setFieldValue,
  errors,
  touched,
}) => {
  const arrayHelpersRef = useRef<any>(null);
  const arrayHelpersRef2 = useRef<any>([]);

  const [inputValue, setInputValue] = useState<any[]>([]);
  const [chips, setChips] = useState<any[]>([]);
  const [options, setOptions] = useState<string[]>([
    'Services',
    'General',
    'Account',
  ]);

  const handleAddChip = (chip: string, idx: number) => {
    // Update options if needed
    if (chip && !options.includes(chip)) {
      setOptions([...options, chip]);
    }

    // Update the chips state
    setChips((prevChips) => {
      const newChips = [...prevChips];
      newChips[idx] = chip;
      return newChips;
    });

    // Update the input values state
    setInputValue((prevInputValue) => {
      const newInputValues = [...prevInputValue];
      newInputValues[idx] = chip;
      return newInputValues;
    });
  };

  useEffect(() => {
    setFieldValue(
      'items',
      values.items.map((item: any, idx: number) => ({
        ...item,
        categoryName: chips[idx] || item.categoryName, // Update categoryName with the chip value or keep existing value
      }))
    );
  }, [chips]);

  useEffect(() => {
    if (response && !isAddMode) {
      const result = response?.value ? JSON.parse(response?.value) : '';
      if (isValidArray(result)) {
        const filteredCategoryNames = result.map(
          (item: any) => item.categoryName
        );
        setOptions((curr) => [...curr, ...filteredCategoryNames]);
        setChips((curr) => [...curr, ...filteredCategoryNames]);
      }
    }
  }, [response, !isAddMode]);

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        variant='h6'
        sx={{ color: 'text.disabled', mb: 2 }}
      >
        Category:
      </Typography>
      <Stack
        divider={
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed' }}
          />
        }
        spacing={3}
      >
        <FieldArray
          name='items'
          render={(arrayHelpers: any) => {
            arrayHelpersRef.current = arrayHelpers;
            return (
              <>
                {values?.items?.map((item: any, idx: any) => (
                  <React.Fragment key={idx}>
                    <Stack
                      alignItems='flex-end'
                      spacing={1.5}
                    >
                      <Grid
                        container
                        spacing={3}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={6}
                        >
                          <IAutocomplete
                            freeSolo
                            // multiple
                            key={`io${options?.length}`}
                            options={options}
                            value={chips[idx] || ''}
                            onChange={(event, newValue) => {
                              if (typeof newValue === 'string') {
                                handleAddChip(newValue, idx);
                              }
                            }}
                            // onChange={(event, newValue) => {
                            //   if (newValue !== null) {
                            //     handleAddChip(newValue as string, idx);
                            //   }
                            // }}
                            inputValue={inputValue[idx] || ''}
                            onInputChange={(event, newInputValue) => {
                              setInputValue((prevInputValue) => {
                                const newInputValues = [...prevInputValue];
                                newInputValues[idx] = newInputValue;
                                return newInputValues;
                              });
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  label={option}
                                  {...getTagProps({ index })}
                                  onDelete={undefined} // Handle chip deletion if needed
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant='outlined'
                                label='FAQ Category'
                                error={Boolean(
                                  touched?.items?.[idx]?.categoryName &&
                                    errors?.items?.[idx]?.categoryName
                                )}
                                helperText={
                                  Boolean(
                                    touched?.items?.[idx]?.categoryName &&
                                      errors?.items?.[idx]?.categoryName
                                  ) && errors.items?.[idx]?.categoryName
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent form submission
                                    handleAddChip(inputValue[idx] || '', idx);
                                  }
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  onBlur: () => {
                                    handleAddChip(inputValue[idx] || '', idx);
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        spacing={3}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={12}
                        >
                          <FieldArray
                            name={`items.[${idx}.qaList]`}
                            render={(arrayHelpers2) => {
                              arrayHelpersRef2.current[idx] = arrayHelpers2;
                              return (
                                <>
                                  {values?.items[idx]?.qaList?.map(
                                    (item: any, index: any) => {
                                      return (
                                        <>
                                          <Stack
                                            alignItems='flex-end'
                                            spacing={1.5}
                                            key={`${idx}.${index}`}
                                          >
                                            <Grid
                                              container
                                              spacing={3}
                                            >
                                              <Grid
                                                item
                                                xs={12}
                                                sm={12}
                                              >
                                                <TextField
                                                  id='question'
                                                  name={`items[${idx}].qaList[${index}].question`}
                                                  value={
                                                    values?.items[idx].qaList[
                                                      index
                                                    ].question ?? ''
                                                  }
                                                  onChange={handleChange}
                                                  label={'Question'}
                                                  fullWidth
                                                  variant='outlined'
                                                  error={Boolean(
                                                    values?.items?.[idx]
                                                      ?.qaList?.[index]
                                                      ?.question &&
                                                      errors?.items?.[idx]
                                                        ?.qaList?.[index]
                                                        ?.question
                                                  )}
                                                  helperText={
                                                    Boolean(
                                                      touched?.items?.[idx]
                                                        ?.qaList?.[index]
                                                        ?.question &&
                                                        errors?.items?.[idx]
                                                          ?.qaList?.[index]
                                                          ?.question
                                                    ) &&
                                                    errors?.items?.[idx]
                                                      ?.qaList?.[index]
                                                      ?.question
                                                  }
                                                />
                                              </Grid>
                                              <Grid
                                                item
                                                xs={12}
                                                sm={12}
                                              >
                                                <TextField
                                                  id='answer'
                                                  name={`items[${idx}].qaList[${index}].answer`}
                                                  value={
                                                    values?.items[idx].qaList[
                                                      index
                                                    ].answer ?? ''
                                                  }
                                                  onChange={handleChange}
                                                  label={'Answer'}
                                                  fullWidth
                                                  variant='outlined'
                                                  error={Boolean(
                                                    values?.items?.[idx]
                                                      ?.qaList?.[index]
                                                      ?.answer &&
                                                      errors?.items?.[idx]
                                                        ?.qaList?.[index]
                                                        ?.answer
                                                  )}
                                                  helperText={
                                                    Boolean(
                                                      touched?.items?.[idx]
                                                        ?.qaList?.[index]
                                                        ?.answer &&
                                                        errors?.items?.[idx]
                                                          ?.qaList?.[index]
                                                          ?.answer
                                                    ) &&
                                                    errors?.items?.[idx]
                                                      ?.qaList?.[index]?.answer
                                                  }
                                                />
                                              </Grid>
                                            </Grid>
                                            <div>
                                              {index + 1 ===
                                                values?.items[idx]?.qaList
                                                  .length && (
                                                <Button
                                                  size='small'
                                                  startIcon={
                                                    <Iconify icon='eva:plus-fill' />
                                                  }
                                                  onClick={() => {
                                                    arrayHelpersRef2.current[
                                                      idx
                                                    ].push({
                                                      question: '',
                                                      answer: '',
                                                    });
                                                  }}
                                                  sx={{
                                                    flexShrink: 0,
                                                    paddingRight: '15px',
                                                  }}
                                                >
                                                  Add Question
                                                </Button>
                                              )}

                                              <Button
                                                size='small'
                                                color='error'
                                                startIcon={
                                                  <Iconify icon='eva:trash-2-outline' />
                                                }
                                                onClick={() => {
                                                  arrayHelpers2?.remove(index);
                                                }}
                                              >
                                                Remove
                                              </Button>
                                            </div>
                                          </Stack>
                                          <Divider
                                            sx={{
                                              my: 3,
                                              borderStyle: 'dashed',
                                            }}
                                          />
                                        </>
                                      );
                                    }
                                  )}
                                </>
                              );
                            }}
                          ></FieldArray>
                        </Grid>
                      </Grid>
                      {
                        <div>
                          {idx + 1 === values?.items?.length && (
                            <Button
                              size='small'
                              startIcon={<Iconify icon='eva:plus-fill' />}
                              onClick={() =>
                                arrayHelpersRef.current.push({
                                  categoryName: '',
                                  qaList: [
                                    {
                                      question: '',
                                      answer: '',
                                    },
                                  ],
                                })
                              }
                              sx={{
                                flexShrink: 0,
                                paddingRight: '15px',
                              }}
                            >
                              New Category
                            </Button>
                          )}
                          <Button
                            size='small'
                            color='error'
                            startIcon={<Iconify icon='eva:trash-2-outline' />}
                            onClick={() => {
                              arrayHelpers?.remove(idx);
                            }}
                            disabled={true}
                          >
                            Remove
                          </Button>
                        </div>
                      }
                    </Stack>
                    <Divider
                      sx={{
                        my: 3,
                        borderStyle: 'dashed',
                      }}
                    />
                  </React.Fragment>
                ))}
              </>
            );
          }}
        ></FieldArray>
      </Stack>
    </Box>
  );
};
export default DetailsForm;
